import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, vModelText as _vModelText, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, withCtx as _withCtx, normalizeClass as _normalizeClass, createBlock as _createBlock, withModifiers as _withModifiers, Teleport as _Teleport } from "vue"

const _hoisted_1 = ["aria-busy"]
const _hoisted_2 = ["disabled"]
const _hoisted_3 = {
  key: 0,
  class: "error",
  role: "alert"
}
const _hoisted_4 = ["disabled"]
const _hoisted_5 = {
  class: "workspace-target",
  "aria-label": "工作区目标配置"
}
const _hoisted_6 = { class: "field" }
const _hoisted_7 = { class: "field" }
const _hoisted_8 = ["value"]
const _hoisted_9 = { class: "import-bar" }
const _hoisted_10 = ["disabled"]
const _hoisted_11 = {
  key: 0,
  class: "notice",
  role: "status"
}
const _hoisted_12 = {
  key: 1,
  class: "error",
  role: "alert"
}
const _hoisted_13 = {
  key: 2,
  class: "skill-import",
  "aria-labelledby": "skill-import-title"
}
const _hoisted_14 = { class: "notice" }
const _hoisted_15 = {
  key: 0,
  class: "notice"
}
const _hoisted_16 = {
  key: 1,
  open: ""
}
const _hoisted_17 = { class: "skill-rows" }
const _hoisted_18 = { class: "skill-name" }
const _hoisted_19 = { class: "skill-ids" }
const _hoisted_20 = { class: "fields" }
const _hoisted_21 = ["for"]
const _hoisted_22 = ["id", "onUpdate:modelValue", "aria-label"]
const _hoisted_23 = ["value"]
const _hoisted_24 = {
  key: 1,
  class: "error",
  role: "alert"
}
const _hoisted_25 = {
  key: 2,
  class: "error",
  role: "alert"
}
const _hoisted_26 = ["disabled"]
const _hoisted_27 = ["disabled"]
const _hoisted_28 = ["disabled"]
const _hoisted_29 = ["disabled"]

import StudioSelectField from "./StudioSelectField.vue";
import StudioHistoryToolbar from "./StudioHistoryToolbar.vue";
import { useStudioHistory } from "./useStudioHistory";
import { useSkillAnimationPresets } from "./EntityPresetEditor/useSkillAnimationPresets";
import type { SkillAnimationPreset } from "./EntityPresetEditor/skillAnimationPresets";
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from "vue";
import { toast } from "vue-sonner";
import { createWorkspaceStructIds, STRUCT_ID_GROUPS, WORKSPACE_STRUCT_ID_FIELDS, validateWorkspaceStructIds,
  type WorkspaceStructIds, type WorkspaceStructIdState, type StructIdCandidate } from "./workspaceStructIds";
import { workspaceFileTypeOptions } from "./versioning/types";
import { createWorkspaceSettings, type WorkspaceSettings } from "./workspaceSettings";
import { workspaceTargetErrors } from "./versioning/catalog";
import WorkspaceVersionSettings from "./WorkspaceVersionSettings.vue";
import WorkspaceGilUiIndexRepair from "./WorkspaceGilUiIndexRepair.vue";
import { isWorkspaceStructGroupAvailable } from "./workspaceFeatures";


export default /*@__PURE__*/_defineComponent({
  __name: 'WorkspaceStructIdSettings',
  props: {
    workspace: {},
    state: {},
    loadError: {},
    settings: {},
    saveSettings: { type: Function },
    finishSettings: { type: Function },
    deleteWorkspace: { type: Function }
  },
  emits: ["close", "retry"],
  setup(__props: any, { emit: __emit }) {

const props = __props;
const emit = __emit;
const draft = ref({ ...props.state.ids });
const draftName = ref(props.workspace);
const draftSettings = ref(createWorkspaceSettings(props.settings));
const visibleStructGroups = computed(() => STRUCT_ID_GROUPS.filter(group => isWorkspaceStructGroupAvailable(group, draftSettings.value)));
const candidates = ref<Record<string, StructIdCandidate[]>>({ ...props.state.candidates });
const { presets: skillPresets, ready: skillsReady, error: skillsError, retry: retrySkills, saveSnapshot: saveSkillsSnapshot } = useSkillAnimationPresets();
const draftSkills = ref<SkillAnimationPreset[]>([]);
const skillOriginalIds = ref<Record<string, string>>({});
const skillMatched = ref<string[]>([]), skillMissing = ref<string[]>([]);
const busy = ref(false), error = ref<string>(""), importMessage = ref("");
const fileInput = ref<HTMLInputElement>(), dialog = ref<HTMLElement>();
const history = useStudioHistory({
  session: false,
  element: dialog, blocked: () => busy.value || !!props.loadError, label: "修改工作区设置",
  capture: () => JSON.stringify({ name: draftName.value, settings: draftSettings.value, ids: draft.value, candidates: candidates.value, importMessage: importMessage.value,
    skills: draftSkills.value, skillOriginalIds: skillOriginalIds.value,
    skillMatched: skillMatched.value, skillMissing: skillMissing.value }),
  restore: snapshot => {
    const state = JSON.parse(snapshot);
    draftName.value = state.name; draftSettings.value = state.settings;
    draft.value = state.ids; candidates.value = state.candidates; importMessage.value = state.importMessage;
    draftSkills.value = state.skills; skillOriginalIds.value = state.skillOriginalIds;
    skillMatched.value = state.skillMatched; skillMissing.value = state.skillMissing;
    error.value = "";
  },
  onError: reason => { error.value = (reason instanceof Error ? reason.message : String(reason)); },
});
const previousFocus = document.activeElement as HTMLElement | null;
const errors = computed(() => [...workspaceTargetErrors(draftSettings.value), ...validateWorkspaceStructIds(draft.value, draftSettings.value)]);
const skillChanges = computed(() => draftSkills.value.filter(preset => preset.configId !== skillOriginalIds.value[preset.id]));
const skillPreview = computed(() => draftSkills.value.filter(preset => skillMatched.value.includes(preset.id)
  || preset.configId !== skillOriginalIds.value[preset.id]));
const skillName = (id: string) => draftSkills.value.find(preset => preset.id === id)?.name || "未命名技能动画";
const fieldsFor = (group: string) => WORKSPACE_STRUCT_ID_FIELDS.filter(field => field.group === group);
const labelFor = (key: string) => { const field = WORKSPACE_STRUCT_ID_FIELDS.find(item => item.key === key)!; return `${field.group} · ${field.label}`; };
function close() { if (!busy.value) emit("close"); }
async function removeWorkspace() {
  if (busy.value) return;
  busy.value = true;
  try { await props.deleteWorkspace(); }
  finally { busy.value = false; }
}
function reset() { draft.value = createWorkspaceStructIds(); candidates.value = {}; error.value = ""; }
async function readGil(event: Event) {
  const input = event.target as HTMLInputElement, file = input.files?.[0];
  input.value = "";
  if (!file) return;
  busy.value = true; error.value = "";
  try {
    if (!/\.gil$/i.test(file.name)) throw new Error("请选择 .gil 存档文件。");
    if (file.size > 100 * 1024 * 1024) throw new Error("GIL 文件超过 100 MB，请选择较小的存档。");
    if (!skillsReady.value && !skillsError.value) throw new Error("技能动画预设正在读取，请稍后重试。");
    const { importGilWorkspaceIds } = await import("./gilSkillAnimations");
    const currentSkills = skillsReady.value ? (draftSkills.value.length ? draftSkills.value : skillPresets.value) : [];
    const { structs: result, skills } = importGilWorkspaceIds(await file.arrayBuffer(), draft.value, currentSkills);
    if (!result.matched.length && !result.ambiguous.length && !skills.matched.length && !skills.ambiguous.length) {
      throw new Error("未找到匹配的 DSFG 结构体或技能动画，现有 ID 已保留。");
    }
    draft.value = result.ids;
    for (const key of result.matched) delete candidates.value[key];
    skillOriginalIds.value = Object.fromEntries(currentSkills.map(preset => [preset.id,
      Object.prototype.hasOwnProperty.call(skillOriginalIds.value, preset.id) ? skillOriginalIds.value[preset.id] : preset.configId]));
    draftSkills.value = skills.presets;
    skillMatched.value = skills.matched; skillMissing.value = skills.missing;
    const conflicts = [
      ...result.ambiguous.map(labelFor),
      ...skills.ambiguous.map(id => `技能动画 · ${skillName(id)}`),
    ];
    if (conflicts.length) toast.warning("GIL 导入存在 ID 冲突", {
      description: `以下项存在多个同名 ID，已跳过并保留原值：${conflicts.join("、")}。`,
      duration: 8000,
    });
    importMessage.value = `「${file.name}」已识别 ${result.matched.length} / ${WORKSPACE_STRUCT_ID_FIELDS.length} 项结构体；`
      + `技能动画 ${skills.matched.length} / ${currentSkills.length} 项，${skillChanges.value.length} 项 ID 待更新。`
      + (result.missing.length ? `未识别项保留原值：${result.missing.map(labelFor).join("、")}。` : "")
      + (!skillsReady.value ? "技能动画预设读取失败，本次仅识别结构体 ID。" : "")
      + "点击保存后应用到当前工作区。";
  } catch (reason) { error.value = reason instanceof Error ? reason.message : String(reason); }
  finally { busy.value = false; }
}
async function save() {
  if (busy.value || props.loadError || errors.value.length) return;
  busy.value = true; error.value = "";
  let structsSaved = false;
  try {
    if (skillChanges.value.length && !skillsReady.value) throw new Error(skillsError.value || "请先重新读取技能动画预设。");
    await props.saveSettings(draft.value, draftSettings.value, draftName.value); structsSaved = true;
    if (skillChanges.value.length) {
      const changes = new Map(skillChanges.value.map(preset => [preset.id, preset.configId]));
      await saveSkillsSnapshot(skillPresets.value.map(preset => changes.has(preset.id) ? { ...preset, configId: changes.get(preset.id)! } : preset));
    }
    await props.finishSettings?.(draftName.value);
    emit("close");
  }
  catch (reason) { error.value = (structsSaved ? "工作区与结构体设置已保存，后续保存未完成，请重试：" : "")
    + (reason instanceof Error ? reason.message : String(reason)); }
  finally { busy.value = false; }
}
function keydown(event: KeyboardEvent) {
  if (event.key === "Escape") { event.preventDefault(); close(); }
  if (event.key !== "Tab") return;
  const controls = [...(dialog.value?.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled):not([type="file"]), select:not(:disabled), [tabindex="0"]') ?? [])];
  const first = controls[0], last = controls[controls.length - 1];
  if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
  else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
}
onMounted(() => { void nextTick(() => dialog.value?.focus()); });
onBeforeUnmount(() => previousFocus?.focus());

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
    _createElementVNode("div", {
      class: "workspace-ids-backdrop dsfg-typography",
      onClick: _withModifiers(close, ["self"]),
      onKeydown: keydown
    }, [
      _createElementVNode("form", {
        ref_key: "dialog",
        ref: dialog,
        class: "workspace-ids",
        role: "dialog",
        "aria-modal": "true",
        "aria-labelledby": "workspace-ids-title",
        tabindex: "-1",
        "aria-busy": busy.value,
        onSubmit: _withModifiers(save, ["prevent"])
      }, [
        _createElementVNode("header", null, [
          _createElementVNode("div", null, [
            _createElementVNode("span", null, _toDisplayString(__props.workspace), 1),
            _cache[7] || (_cache[7] = _createElementVNode("h2", { id: "workspace-ids-title" }, "工作区设置", -1))
          ]),
          _createVNode(StudioHistoryToolbar, {
            local: "",
            history: _unref(history),
            disabled: busy.value || !!__props.loadError
          }, null, 8, ["history", "disabled"]),
          _createElementVNode("button", {
            type: "button",
            disabled: busy.value,
            "aria-label": "关闭工作区设置",
            onClick: close
          }, "×", 8, _hoisted_2)
        ]),
        _cache[15] || (_cache[15] = _createElementVNode("p", { class: "intro" }, "此工作区的所有模块共用文件类型、基准版本、扩展包和结构体 ID。", -1)),
        (__props.loadError)
          ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
              _createTextVNode(_toDisplayString(__props.loadError) + " ", 1),
              _createElementVNode("button", {
                type: "button",
                onClick: _cache[0] || (_cache[0] = ($event: any) => (emit('retry')))
              }, "重新读取")
            ]))
          : _createCommentVNode("", true),
        _createElementVNode("fieldset", {
          disabled: busy.value || !!__props.loadError
        }, [
          _createElementVNode("section", _hoisted_5, [
            _createElementVNode("div", _hoisted_6, [
              _cache[8] || (_cache[8] = _createElementVNode("label", { for: "workspace-name" }, "工作区名称", -1)),
              _withDirectives(_createElementVNode("input", {
                id: "workspace-name",
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((draftName).value = $event)),
                required: "",
                autocomplete: "off"
              }, null, 512), [
                [_vModelText, draftName.value]
              ])
            ]),
            _createElementVNode("div", _hoisted_7, [
              _cache[9] || (_cache[9] = _createElementVNode("label", { for: "workspace-file-type" }, "项目文件类型", -1)),
              _createVNode(StudioSelectField, {
                id: "workspace-file-type",
                modelValue: draftSettings.value.fileType,
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((draftSettings.value.fileType) = $event)),
                to: false,
                disabled: busy.value || !!__props.loadError,
                "aria-label": "项目文件类型"
              }, {
                default: _withCtx(() => [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(workspaceFileTypeOptions), (option) => {
                    return (_openBlock(), _createElementBlock("option", {
                      key: option.value,
                      value: option.value
                    }, _toDisplayString(option.label), 9, _hoisted_8))
                  }), 128))
                ]),
                _: 1
              }, 8, ["modelValue", "disabled"])
            ]),
            _createVNode(WorkspaceVersionSettings, {
              modelValue: draftSettings.value,
              "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((draftSettings).value = $event)),
              disabled: busy.value || !!__props.loadError
            }, null, 8, ["modelValue", "disabled"])
          ]),
          _cache[14] || (_cache[14] = _createElementVNode("h3", null, "结构体 ID", -1)),
          _createElementVNode("div", _hoisted_9, [
            _cache[10] || (_cache[10] = _createElementVNode("div", null, [
              _createElementVNode("strong", null, "从 GIL 自动识别"),
              _createElementVNode("p", null, "按名称匹配结构体 ID 和技能动画配置 ID，保存后应用到当前工作区。文件在本机读取。")
            ], -1)),
            _createElementVNode("button", {
              type: "button",
              disabled: !_unref(skillsReady) && !_unref(skillsError),
              onClick: _cache[4] || (_cache[4] = ($event: any) => (fileInput.value?.click()))
            }, _toDisplayString(busy.value ? '读取中…' : '读取 GIL'), 9, _hoisted_10),
            _createElementVNode("input", {
              ref_key: "fileInput",
              ref: fileInput,
              type: "file",
              accept: ".gil",
              hidden: "",
              onChange: readGil
            }, null, 544)
          ]),
          _createVNode(WorkspaceGilUiIndexRepair, {
            disabled: busy.value || !!__props.loadError,
            onBusy: _cache[5] || (_cache[5] = ($event: any) => (busy.value = $event))
          }, null, 8, ["disabled"]),
          (importMessage.value)
            ? (_openBlock(), _createElementBlock("p", _hoisted_11, _toDisplayString(importMessage.value), 1))
            : _createCommentVNode("", true),
          (_unref(skillsError))
            ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                _createTextVNode("技能动画预设：" + _toDisplayString(_unref(skillsError)) + " ", 1),
                _createElementVNode("button", {
                  type: "button",
                  onClick: _cache[6] || (_cache[6] = ($event: any) => (_unref(retrySkills)().catch(() => undefined)))
                }, "重新读取／保存")
              ]))
            : _createCommentVNode("", true),
          (draftSkills.value.length)
            ? (_openBlock(), _createElementBlock("section", _hoisted_13, [
                _cache[12] || (_cache[12] = _createElementVNode("h3", { id: "skill-import-title" }, "技能动画配置 ID", -1)),
                _createElementVNode("p", _hoisted_14, "已匹配 " + _toDisplayString(skillMatched.value.length) + " 项，" + _toDisplayString(skillChanges.value.length) + " 项 ID 待更新。只更新预设，已有 Timeline 动作的技能 ID 保持原值。", 1),
                (skillMissing.value.length)
                  ? (_openBlock(), _createElementBlock("p", _hoisted_15, "未识别项保留原值：" + _toDisplayString(skillMissing.value.map(skillName).join('、')) + "。", 1))
                  : _createCommentVNode("", true),
                (skillPreview.value.length)
                  ? (_openBlock(), _createElementBlock("details", _hoisted_16, [
                      _createElementVNode("summary", null, "查看技能动画 ID（" + _toDisplayString(skillPreview.value.length) + " 项）", 1),
                      _createElementVNode("div", _hoisted_17, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(skillPreview.value, (preset) => {
                          return (_openBlock(), _createElementBlock("div", {
                            key: preset.id,
                            class: _normalizeClass(["skill-row", { changed: preset.configId !== skillOriginalIds.value[preset.id] }])
                          }, [
                            _createElementVNode("span", _hoisted_18, _toDisplayString(preset.name), 1),
                            _createElementVNode("span", _hoisted_19, [
                              _createElementVNode("code", null, _toDisplayString(skillOriginalIds.value[preset.id] || '未填写'), 1),
                              _cache[11] || (_cache[11] = _createElementVNode("span", { "aria-hidden": "true" }, " → ", -1)),
                              _createElementVNode("code", null, _toDisplayString(preset.configId || '未填写'), 1)
                            ])
                          ], 2))
                        }), 128))
                      ])
                    ]))
                  : _createCommentVNode("", true)
              ]))
            : _createCommentVNode("", true),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.state.warnings, (warning) => {
            return (_openBlock(), _createElementBlock("p", {
              key: warning,
              class: "notice"
            }, _toDisplayString(warning), 1))
          }), 128)),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleStructGroups.value, (group) => {
            return (_openBlock(), _createElementBlock("section", { key: group }, [
              _createElementVNode("h3", null, _toDisplayString(group), 1),
              _createElementVNode("div", _hoisted_20, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(fieldsFor(group), (field) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: field.key,
                    class: "field"
                  }, [
                    _createElementVNode("label", {
                      for: `struct-${field.key}`
                    }, [
                      _createTextVNode(_toDisplayString(field.label), 1),
                      _createElementVNode("small", null, _toDisplayString(field.description), 1)
                    ], 8, _hoisted_21),
                    _withDirectives(_createElementVNode("input", {
                      id: `struct-${field.key}`,
                      "onUpdate:modelValue": ($event: any) => ((draft.value[field.key]) = $event),
                      "aria-label": `${group} · ${field.label} ID`,
                      inputmode: "numeric",
                      autocomplete: "off",
                      placeholder: "填写结构体 ID"
                    }, null, 8, _hoisted_22), [
                      [_vModelText, draft.value[field.key]]
                    ]),
                    (candidates.value[field.key]?.length > 1)
                      ? (_openBlock(), _createBlock(StudioSelectField, {
                          key: 0,
                          modelValue: draft.value[field.key],
                          "onUpdate:modelValue": ($event: any) => ((draft.value[field.key]) = $event),
                          to: false,
                          "aria-label": `${group} · ${field.label} 候选 ID`
                        }, {
                          default: _withCtx(() => [
                            _cache[13] || (_cache[13] = _createElementVNode("option", {
                              disabled: "",
                              value: ""
                            }, "存在不同 ID，请选择或手动填写", -1)),
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(candidates.value[field.key], (candidate) => {
                              return (_openBlock(), _createElementBlock("option", {
                                key: candidate.id,
                                value: candidate.id
                              }, _toDisplayString(candidate.id) + " · " + _toDisplayString(candidate.source), 9, _hoisted_23))
                            }), 128))
                          ]),
                          _: 2
                        }, 1032, ["modelValue", "onUpdate:modelValue", "aria-label"]))
                      : _createCommentVNode("", true)
                  ]))
                }), 128))
              ])
            ]))
          }), 128))
        ], 8, _hoisted_4),
        (errors.value.length)
          ? (_openBlock(), _createElementBlock("div", _hoisted_24, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(errors.value, (message) => {
                return (_openBlock(), _createElementBlock("p", { key: message }, _toDisplayString(message), 1))
              }), 128))
            ]))
          : _createCommentVNode("", true),
        (error.value)
          ? (_openBlock(), _createElementBlock("p", _hoisted_25, _toDisplayString(error.value), 1))
          : _createCommentVNode("", true),
        _createElementVNode("footer", null, [
          _createElementVNode("div", null, [
            _createElementVNode("button", {
              type: "button",
              class: "danger",
              disabled: busy.value,
              onClick: removeWorkspace
            }, "删除工作区", 8, _hoisted_26),
            _createElementVNode("button", {
              type: "button",
              disabled: busy.value || !!__props.loadError,
              onClick: reset
            }, "恢复默认结构体 ID", 8, _hoisted_27)
          ]),
          _createElementVNode("div", null, [
            _createElementVNode("button", {
              type: "button",
              disabled: busy.value,
              onClick: close
            }, "取消", 8, _hoisted_28),
            _createElementVNode("button", {
              type: "submit",
              class: "primary",
              disabled: busy.value || !!__props.loadError || !!errors.value.length || !draftName.value.trim()
            }, _toDisplayString(busy.value ? '处理中…' : '保存设置'), 9, _hoisted_29)
          ])
        ])
      ], 40, _hoisted_1)
    ], 32)
  ]))
}
}

})